import { useEffect, useRef, useState } from "react";
import type { Status } from "@onetasklist/shared";
import type { ConnectionEditState } from "../Connections.ViewModel";

export interface ConfigureConnectionStatusOption {
  id: string;
  label: string;
}

interface UseConfigureConnectionViewModelArgs {
  draftConnection: ConnectionEditState;
  statuses: Status[];
  usesDialogAuthFlow: boolean;
  onConnect: () => Promise<void>;
  onSourceSetupChange?: (
    setup: Record<string, string>,
    isValid: boolean,
  ) => void;
  defaultColumnPlaceholder: string;
}

export interface ConfigureConnectionViewModel {
  isConnected: boolean;
  /** Signed in before but no longer authorised: the settings can't load until Change account. */
  needsReauthorisation: boolean;
  privacyExpanded: boolean;
  togglePrivacyExpanded: () => void;
  sourceSetupValid: boolean;
  handleSourceSetupChange: (
    setup: Record<string, string>,
    isValid: boolean,
  ) => void;
  authSetupDialogOpen: boolean;
  closeAuthSetupDialog: () => void;
  isHelpPanelOpen: boolean;
  openHelpPanel: () => void;
  closeHelpPanel: () => void;
  defaultStatusQuery: string;
  setDefaultStatusQuery: (query: string) => void;
  defaultStatusOptions: ConfigureConnectionStatusOption[];
  selectedStatusLabel: string;
  handleConnectClick: () => void;
  handleChangeAccountClick: () => void;
  confirmAuthSetupDialog: () => Promise<void>;
}

export const useConfigureConnectionViewModel = ({
  draftConnection,
  statuses,
  usesDialogAuthFlow,
  onConnect,
  onSourceSetupChange,
  defaultColumnPlaceholder,
}: UseConfigureConnectionViewModelArgs): ConfigureConnectionViewModel => {
  const [defaultStatusQuery, setDefaultStatusQuery] = useState("");
  const [privacyExpanded, setPrivacyExpanded] = useState(true);
  const [sourceSetupValid, setSourceSetupValid] = useState(true);
  const [authSetupDialogOpen, setAuthSetupDialogOpen] = useState(false);
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);

  const onSourceSetupChangeRef = useRef(onSourceSetupChange);
  useEffect(() => {
    onSourceSetupChangeRef.current = onSourceSetupChange;
  });

  // Tracks which action (connect / change account) triggered the dialog
  const authSetupDialogActionRef = useRef<() => Promise<void>>(onConnect);

  const openAuthSetupDialog = (action: () => Promise<void>): void => {
    authSetupDialogActionRef.current = action;
    setAuthSetupDialogOpen(true);
  };

  const handleSourceSetupChange = (
    setup: Record<string, string>,
    isValid: boolean,
  ): void => {
    setSourceSetupValid(isValid);
    onSourceSetupChangeRef.current?.(setup, isValid);
  };

  const isConnected =
    draftConnection.isAuthorised || !!draftConnection.profileName;
  const needsReauthorisation =
    !draftConnection.isAuthorised && !!draftConnection.profileName;

  useEffect(() => {
    if (isConnected) {
      setPrivacyExpanded(false);
    }
  }, [isConnected]);

  const defaultStatusOptions = statuses
    .filter(
      (s) =>
        !defaultStatusQuery ||
        s.name.toLowerCase().includes(defaultStatusQuery.toLowerCase()),
    )
    .map((s) => ({ id: s.id, label: s.name }));

  const selectedStatusLabel =
    statuses.find((s) => s.id === draftConnection.defaultStatusId)?.name ??
    defaultColumnPlaceholder;

  const dispatchAuthAction = (action: () => Promise<void>): void => {
    if (usesDialogAuthFlow) {
      openAuthSetupDialog(action);
    } else {
      void action();
    }
  };

  const confirmAuthSetupDialog = async (): Promise<void> => {
    setAuthSetupDialogOpen(false);
    await authSetupDialogActionRef.current();
  };

  return {
    isConnected,
    needsReauthorisation,
    privacyExpanded,
    togglePrivacyExpanded: () => setPrivacyExpanded((v) => !v),
    sourceSetupValid,
    handleSourceSetupChange,
    authSetupDialogOpen,
    closeAuthSetupDialog: () => setAuthSetupDialogOpen(false),
    isHelpPanelOpen,
    openHelpPanel: () => setIsHelpPanelOpen(true),
    closeHelpPanel: () => setIsHelpPanelOpen(false),
    defaultStatusQuery,
    setDefaultStatusQuery,
    defaultStatusOptions,
    selectedStatusLabel,
    handleConnectClick: () => dispatchAuthAction(onConnect),
    handleChangeAccountClick: () => dispatchAuthAction(onConnect),
    confirmAuthSetupDialog,
  };
};
